import { useRouter } from 'next/router'
import React from 'react'
import { UseCurrentRefinementsProps, useClearRefinements, useCurrentRefinements } from 'react-instantsearch'

import { Country as CountryList } from 'country-list'
import _, { round } from 'lodash'
import ArrowCircleClockwise from 'public/images/icons/arrows/arrow-circle-clockwise.svg'

import { FILTER_QUERY_LIST } from 'components/_pages/search/refinement-by-query/constants'
import {
  checkQueryExists,
  removeQueryParams,
  toggleQueryParam,
} from 'components/_pages/search/refinement-by-query/utils'
import Button from 'components/button'

import { useTranslate } from 'hooks'
import { tranOnSalePercentageLabel } from 'hooks/filters/use-on-sale-percentage-filter'
import { Country } from 'hooks/retailer/use-countries'

import { INSTANT_SEARCH_ATTRIBUTE_MAP } from 'constants/instantSearch'

import css from './refinement-current-customized.module.scss'
import CurrentRefinementItem from './refinement-current-item'

type CurrentRefinementsCustomizedProps = {
  wholeSalePriceRate: number
  movSalePriceRate: number
  countriesByCode: Record<string, CountryList | Country>
  currency: string
} & UseCurrentRefinementsProps

const excludedAttributes = [
  INSTANT_SEARCH_ATTRIBUTE_MAP['categories.en-HK.lvl0'].key,
  INSTANT_SEARCH_ATTRIBUTE_MAP['categories.zh-HK.lvl0'].key,
  INSTANT_SEARCH_ATTRIBUTE_MAP.query.key,
]

const CurrentRefinementsCustomized = ({
  wholeSalePriceRate = 1,
  movSalePriceRate = 1,
  countriesByCode,
  currency,
  ...props
}: CurrentRefinementsCustomizedProps) => {
  const { t, lang } = useTranslate('strapi-search-[...term]')
  const router = useRouter()

  const { items, refine } = useCurrentRefinements({
    ...props,
    excludedAttributes,
  })

  const { refine: clearRefine, canRefine } = useClearRefinements({
    excludedAttributes,
  })

  const isQueryRefinementOnly =
    items.some((item) => item.attribute === INSTANT_SEARCH_ATTRIBUTE_MAP.query.key) && items.length === 1

  const handleClearRefinement = async () => {
    await removeQueryParams(router, _.map(FILTER_QUERY_LIST, 'name'))
    clearRefine()
  }

  const isFilterQueryExist = FILTER_QUERY_LIST.some(({ name }) => checkQueryExists(router, name))

  return (
    <ul className={css.container}>
      {items.map((item) => {
        const { key: filterKey, locale: filterLocale } =
          INSTANT_SEARCH_ATTRIBUTE_MAP[item.label as keyof typeof INSTANT_SEARCH_ATTRIBUTE_MAP]

        if (filterKey === INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.on-sale-percentage'].key) {
          // display the first refinement only
          const label: string | number = item.refinements[0].label
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [sign, percentage] = label.split(' ')
          return (
            <CurrentRefinementItem
              key={filterKey}
              handleRemove={() => {
                item.refinements.map((refinement) => {
                  refine(refinement)
                })
              }}
              label={tranOnSalePercentageLabel(Number(percentage), t, lang)}
              filterName={t(filterLocale)}
            />
          )
        }

        return item.refinements.map((refinement) => {
          let label: string | number = refinement.label

          switch (filterKey) {
            case INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.universal-brand-mov'].key: {
              const [sign] = label.split(' ')
              label = `${sign} ${round(Number(refinement.value) * movSalePriceRate, 0).toString()} ${
                movSalePriceRate !== 1 ? currency : 'USD'
              }`
              break
            }
            case INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.universal-msrp'].key:
            case INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.universal-price'].key: {
              const [sign] = label.split(' ')
              label = `${sign} ${round(Number(refinement.value) * wholeSalePriceRate, 0).toString()} ${
                movSalePriceRate !== 1 ? currency : 'USD'
              }`
              break
            }
            case INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.made-in'].key:
            case INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.brand-location'].key: {
              const country = countriesByCode[refinement.label] as Country | undefined
              label = country ? country.name : refinement.label
              break
            }
            case INSTANT_SEARCH_ATTRIBUTE_MAP.query.key:
              return
            default:
              break
          }

          const isShowFilterName = item.attribute !== INSTANT_SEARCH_ATTRIBUTE_MAP['attributes.brand-name'].key
          return (
            <CurrentRefinementItem
              key={label}
              handleRemove={() => {
                refine(refinement)
              }}
              label={label}
              filterName={isShowFilterName ? t(filterLocale) : undefined}
            />
          )
        })
      })}

      {FILTER_QUERY_LIST.map(({ name, queryOnValue, locale }) => {
        const hasQueryParam = router.query[name] === queryOnValue
        return (
          hasQueryParam && (
            <CurrentRefinementItem
              key={name}
              handleRemove={() => {
                toggleQueryParam(router, name, queryOnValue)
              }}
              label={t(locale)}
            />
          )
        )
      })}

      {((!!canRefine && !isQueryRefinementOnly) || !!isFilterQueryExist) && (
        <li>
          <Button
            iconLeft={<ArrowCircleClockwise />}
            label={t('PageSearch.PageSearch_Filters_clear_all')}
            style="ghost"
            className={css.btnClearAll}
            onClick={handleClearRefinement}
          />
        </li>
      )}
    </ul>
  )
}

export default CurrentRefinementsCustomized
